import React, { useState, useEffect } from 'react';
import { useGame } from '../state/gameStore.tsx';
import { ShipDesignerView } from './ShipDesignerView.tsx';
import { MetalIcon, He3Icon, CreditsIcon, ShipyardIcon } from '../assets/icons.tsx';
import { QuickBoostModal } from './QuickBoostModal.tsx';

export const ShipyardView: React.FC = () => {
  const { state, produceShips } = useGame();
  const [subTab, setSubTab] = useState<'build' | 'design'>('build');
  const [producingDesignId, setProducingDesignId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [quickBoostTarget, setQuickBoostTarget] = useState<{ id: string; name: string; remainingSec: number } | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!state) return null;

  const { blueprints, inventory, shipyard_queue, buildings, planet } = state;
  const currentMetal = planet.resources.metal;
  const currentHe3 = planet.resources.he3;
  const currentCredits = planet.resources.credits;

  const shipyard = buildings.find((b) => b.type === 'shipyard');
  const shipyardLevel = shipyard?.level ?? 0;

  const handleProduce = async (designId: string, count: number) => {
    try {
      setProducingDesignId(designId);
      await produceShips(designId, count);
    } catch {
      // Handled in store
    } finally {
      setProducingDesignId(null);
    }
  };

  return (
    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Subtab Toggle */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="sci-btn"
          onClick={() => setSubTab('build')}
          style={{
            flex: 1,
            background: subTab === 'build' ? 'rgba(0, 229, 255, 0.15)' : 'var(--bg-panel-subtle)',
            borderColor: subTab === 'build' ? 'var(--accent-cyan)' : 'var(--border-panel)',
            color: subTab === 'build' ? 'var(--accent-cyan)' : 'var(--text-dim)',
            whiteSpace: 'nowrap',
            fontSize: 12,
            padding: '8px 4px',
          }}
        >
          🏭 ĐÓNG TÀU CHIẾN
        </button>
        <button
          className="sci-btn"
          onClick={() => setSubTab('design')}
          style={{
            flex: 1,
            background: subTab === 'design' ? 'rgba(0, 229, 255, 0.15)' : 'var(--bg-panel-subtle)',
            borderColor: subTab === 'design' ? 'var(--accent-cyan)' : 'var(--border-panel)',
            color: subTab === 'design' ? 'var(--accent-cyan)' : 'var(--text-dim)',
            whiteSpace: 'nowrap',
            fontSize: 12,
            padding: '8px 4px',
          }}
        >
          ✏️ THIẾT KẾ MẪU MỚI
        </button>
      </div>

      {subTab === 'design' ? (
        <ShipDesignerView onBlueprintSaved={() => setSubTab('build')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Active Queue if any */}
          {shipyard_queue.length > 0 && (
            <div className="sci-card" style={{ border: '1px solid var(--accent-amber)', background: 'rgba(255, 179, 0, 0.05)' }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-amber)', marginBottom: 6 }}>
                ⏳ HÀNG CHỜ ĐÓNG TÀU ({shipyard_queue.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {shipyard_queue.map((q) => {
                  const bp = blueprints.find((b) => b.id === q.design_id);
                  const remainingSec = Math.max(0, Math.ceil((q.finish_at - now) / 1000));
                  return (
                    <div key={q.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, flexWrap: 'wrap', gap: 6 }}>
                      <span style={{ fontWeight: 600 }}>
                        {bp?.name || 'Chiến hạm'} × {q.count}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="mono" style={{ color: 'var(--accent-amber)', fontWeight: 700 }}>
                          ⏳ {remainingSec}s
                        </span>
                        <button
                          className="sci-btn"
                          onClick={() => setQuickBoostTarget({ id: q.id, name: `${bp?.name || 'Chiến hạm'} × ${q.count}`, remainingSec })}
                          style={{
                            padding: '2px 8px',
                            fontSize: 10,
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.3) 100%)',
                            color: '#60a5fa',
                            borderColor: '#3b82f6',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ⚡ TĂNG TỐC
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Blueprint Cards */}
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-bright)', marginBottom: 6 }}>
              DANH SÁCH MẪU TÀU ĐÃ LƯU
            </h3>
            {blueprints.length === 0 ? (
              <div className="sci-card" style={{ textAlign: 'center', padding: '24px' }}>
                <p style={{ color: 'var(--text-dim)', marginBottom: 10 }}>Chưa có bản thiết kế tàu chiến nào!</p>
                <button className="sci-btn" onClick={() => setSubTab('design')}>
                  TẠO BẢN THIẾT KẾ ĐẦU TIÊN
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {blueprints.map((bp) => {
                  const ownedCount = inventory[bp.id] || 0;
                  const stats = bp.stats;

                  const cost1 = stats.unit_cost;
                  const canAfford1 = currentMetal >= cost1.metal && currentHe3 >= cost1.he3 && currentCredits >= cost1.credits;
                  const canAfford5 = currentMetal >= cost1.metal * 5 && currentHe3 >= cost1.he3 * 5 && currentCredits >= cost1.credits * 5;

                  const isVoidStrikerSkin = state.player.equipped_ship_skin === 'skin_ship_void_striker' || state.player.equipped_cosmetics?.ship_skin === 'skin_ship_void_striker';

                  return (
                    <div
                      key={bp.id}
                      className="sci-card"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        borderColor: isVoidStrikerSkin ? 'rgba(168, 85, 247, 0.4)' : undefined,
                        boxShadow: isVoidStrikerSkin ? '0 0 10px rgba(168, 85, 247, 0.15)' : undefined,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <h4 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-bright)', margin: 0 }}>
                              {bp.name}
                            </h4>
                            {isVoidStrikerSkin && (
                              <span style={{
                                background: 'rgba(168, 85, 247, 0.2)',
                                border: '1px solid #a855f7',
                                color: '#c084fc',
                                fontSize: 10,
                                fontWeight: 700,
                                padding: '1px 5px',
                                borderRadius: 3,
                              }}>
                                🛸 VOID STRIKER
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase' }}>
                            Lớp thân: {bp.hull_id} | Sát thương: {stats.total_damage} | Máu: {stats.total_hp}
                          </span>
                        </div>

                        <div className="mono" style={{
                          fontSize: 12,
                          color: 'var(--accent-cyan)',
                          background: 'rgba(0, 229, 255, 0.1)',
                          border: '1px solid rgba(0, 229, 255, 0.2)',
                          padding: '3px 8px',
                          borderRadius: 4,
                          fontWeight: 700,
                        }}>
                          Trong kho: {ownedCount} tàu
                        </div>
                      </div>

                      {/* Cost breakdown */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border-panel)', paddingTop: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
                          <span style={{ color: 'var(--text-dim)' }}>Đơn giá:</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <MetalIcon size={12} />
                            <span className="mono">{cost1.metal}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <He3Icon size={12} />
                            <span className="mono">{cost1.he3}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CreditsIcon size={12} />
                            <span className="mono">{cost1.credits}</span>
                          </div>
                        </div>

                        {/* Batch Produce Buttons */}
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="sci-btn"
                            disabled={!canAfford1 || producingDesignId === bp.id}
                            onClick={() => handleProduce(bp.id, 1)}
                            style={{ padding: '4px 8px', fontSize: 11 }}
                          >
                            ĐÓNG 1
                          </button>
                          <button
                            className="sci-btn sci-btn-amber"
                            disabled={!canAfford5 || producingDesignId === bp.id}
                            onClick={() => handleProduce(bp.id, 5)}
                            style={{ padding: '4px 8px', fontSize: 11 }}
                          >
                            ĐÓNG 5
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {quickBoostTarget && (
        <QuickBoostModal
          targetType="shipyard"
          targetId={quickBoostTarget.id}
          targetName={quickBoostTarget.name}
          remainingSeconds={quickBoostTarget.remainingSec}
          onClose={() => setQuickBoostTarget(null)}
          onSuccess={() => setQuickBoostTarget(null)}
        />
      )}
    </div>
  );
};
